import { useEffect, useState } from 'react'
import { Button, Divider, Stack, Typography } from '@mui/material'
import { Loading } from 'components/Loading'
import { Error } from 'components/Error'
import { StickyHeader } from 'components/StickyHeader'
import PageWrapper from 'hoc/PageWrapper'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useLotQuery } from 'services/lots'
import { TenderCountdown } from '../tender-countdown'
import { useTenderQuery } from 'services/tender'
import { ITender } from 'types/tender'
import { ILot } from 'types/lots'
import { LotInformation } from './lot-infromation'
import ConfirmationDialog from 'components/ConfirmationDialog'
import { useBoolean } from 'hooks/useBoolean'
import { OfferCreation } from './offer-creation'
import {
  useCreateSubmissionMutation,
  useMySubmissionsQuery,
  useSubmitSubmissionMutation,
} from 'services/submissions'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'
import { SubmissionStatus } from 'types/submissions'
import { hasTenderPassedStartDate } from 'utils/tender'
import { useUserCurrentQuery } from 'services/users'
import { EOrganizationType } from 'types/users'
import { useTenderPassedDocDeadline } from 'hooks/date/useTenderPassedDocDeadline'

type Props = {
  tender: ITender
  lot: ILot
}

const TenderParticipationLot = ({ tender, lot }: Props) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const dialogJoin = useBoolean(false)
  const { data: currentUser } = useUserCurrentQuery()
  const [isSubmissionConfirmationOpen, setSubmissionConfirmationOpen] =
    useState(false)

  const { data: submission } = useMySubmissionsQuery({ lotId: lot.id })
  const [createSubmission, { isSuccess, isLoading, isError }] =
    useCreateSubmissionMutation()
  const [submit, { isLoading: isSubmitLoading }] = useSubmitSubmissionMutation()
  const isClient = currentUser?.organizationType === EOrganizationType.CLIENT
  const hasDocDeadlinePassed = useTenderPassedDocDeadline(tender)

  const onCreateSubmission = () => {
    createSubmission({
      id: uuidv4(),
      lotId: lot.id,
      status: SubmissionStatus.CREATED,
      technicalSubmission: null,
      procurementSubmission: null,
      documentSubmissions: null,
    })
  }

  useEffect(() => {
    if (isSuccess) {
      dialogJoin.setFalse()
      toast.success(t('operation-success'))
    }
    if (isError) {
      toast.error(t('failedToCreate'))
    }
  }, [isSuccess, isError, t, dialogJoin])

  if (!submission) return <></>

  const handleSend = () => {
    if (submission) {
      submit({ submissionId: submission.id })
        .then((res) => {
          if ('error' in res) {
            toast.error(t('failedToCreate'))
          } else {
            toast.success(t('submission-successful'))
            navigate(-1)
          }
        })
        .catch(() => toast.error(t('failedToCreate')))
    }
  }

  return (
    <>
      {dialogJoin.isTrue && (
        <ConfirmationDialog
          open
          title={t('termsAndConditions')}
          text={t('termsAndConditionsDescription')}
          confirmText={t('accept')}
          onClose={dialogJoin.setFalse}
          isLoading={isLoading}
          onConfirm={onCreateSubmission}
        />
      )}
      {isSubmissionConfirmationOpen && (
        <ConfirmationDialog
          open
          title={t('submit')}
          text={t('aware-to-submit')}
          confirmText={t('send')}
          onClose={() => setSubmissionConfirmationOpen(false)}
          isLoading={isSubmitLoading}
          onConfirm={handleSend}
        />
      )}
      <PageWrapper>
        <Stack spacing={2}>
          <StickyHeader
            title={t('lotSettings')}
            headerActions={
              <Stack direction="row" spacing={1}>
                {isClient && (
                  <Button
                    disabled={isSubmitLoading || hasDocDeadlinePassed}
                    onClick={() => setSubmissionConfirmationOpen(true)}
                    variant="contained"
                    style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                  >
                    {t('send')}
                  </Button>
                )}
                <Button
                  disabled={isSubmitLoading}
                  onClick={() => navigate(-1)}
                  variant="outlined"
                >
                  {t('back')}
                </Button>
              </Stack>
            }
          />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ backgroundColor: 'primary.main', p: 2 }}
          >
            <Typography variant="body2" color="white">
              {`${t('lot-name')}: ${lot.name}`}
            </Typography>
          </Stack>
          <Divider flexItem />
          <TenderCountdown tender={tender} />
          <Divider flexItem />
          <LotInformation lot={lot} />
          {submission ? (
            <OfferCreation lot={lot} tender={tender} />
          ) : (
            <Stack alignItems="center">
              <Button
                variant="contained"
                style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                onClick={dialogJoin.setTrue}
              >
                {t('join')}
              </Button>
            </Stack>
          )}
        </Stack>
      </PageWrapper>
    </>
  )
}

export const TenderParticipationLotPage = () => {
  const { lotId, tenderId } = useParams()

  const {
    data: lot,
    isLoading: isLotLoading,
    isError: isLotError,
  } = useLotQuery({
    id: lotId ?? '',
  })

  const {
    data: tender,
    isLoading: isTenderLoading,
    isError: isTenderError,
  } = useTenderQuery({ id: tenderId ?? '' })

  if (tender) {
    if (!tender.startTime || !hasTenderPassedStartDate(tender)) {
      return <Navigate to={`/portal/tender-participation/${tender.id}`} />
    }
  }

  if (isLotLoading && isTenderLoading) return <Loading />

  if (isLotError && isTenderError) return <Error />

  if (lot === undefined || tender === undefined) {
    return <></>
  }

  return <TenderParticipationLot tender={tender} lot={lot} />
}
